/**
 * WorkflowBar Component - Dynamic workflow steps with status indicators
 * Displays workflow steps with variable vertical line heights based on description length
 */

import React from 'react';

export interface WorkflowStep {
  id: string;
  title: string;
  description: string;
  status: 'completed' | 'in-progress' | 'upcoming';
  icon?: string;
}

interface WorkflowBarProps {
  steps: WorkflowStep[];
  className?: string;
}

const WorkflowBar: React.FC<WorkflowBarProps> = ({ steps, className = '' }) => {
  const getStatusIcon = (status: WorkflowStep['status'], customIcon?: string) => {
    if (customIcon) return customIcon;
    
    switch (status) {
      case 'completed':
        return '✓';
      case 'in-progress':
        return '◐';
      case 'upcoming':
        return '○';
      default:
        return '○';
    }
  };

  const getStatusClass = (status: WorkflowStep['status']) => {
    switch (status) {
      case 'completed':
        return 'completed';
      case 'in-progress':
        return 'in-progress';
      case 'upcoming':
        return 'upcoming';
      default:
        return 'upcoming';
    }
  };

  // Calculate line height based on description length
  const calculateLineHeight = (description: string, index: number, totalSteps: number) => {
    if (index === totalSteps - 1) return 0; // No line for the last step
    
    const baseHeight = 40;
    const lengthMultiplier = Math.max(1, Math.ceil(description.length / 50));
    return baseHeight + (lengthMultiplier * 20);
  };

  return (
    <div className={`workflow-bar ${className}`}>
      <div className="workflow-steps">
        {steps.map((step, index) => {
          const lineHeight = calculateLineHeight(step.description, index, steps.length);
          
          return (
            <div key={step.id} className="workflow-step">
              <div className="step-indicator-container">
                <div className={`step-indicator ${getStatusClass(step.status)}`}>
                  <span className="step-icon">
                    {getStatusIcon(step.status, step.icon)}
                  </span>
                </div>
                {index < steps.length - 1 && (
                  <div 
                    className="step-line"
                    style={{ height: `${lineHeight}px` }}
                  />
                )}
              </div>
              
              <div className="step-content">
                <h3 className="step-title">{step.title}</h3>
                <p className="step-description">{step.description}</p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default WorkflowBar;